import type { IGroupChatUser } from "@common/types/api/r2-group-chat-types";
import { GroupChatBase } from "@core/base/group-chat-base";
import { OfficeService } from "@core/office/office-service/office-service";
import { OfficeSessionService } from "@core/office/office-session/office.session.service";
import type { IGroupChatSendResult, IGroupChatUiMessage } from "@core/types/group-chat-message.types";
import { SessionCryptoService } from "@services/session-crypto/session-crypto.service";
import { Observable } from "rxjs";
import { OfficeGroupChatAdapterServiceV1 } from "./office-group-chat-v1";

export class OfficeGroupChat extends GroupChatBase
{
    private adapter = new OfficeGroupChatAdapterServiceV1(
        this.office,
        this.session,
        this.sessionCrypto,
    );

    constructor(
        private office: OfficeService,
        private session: OfficeSessionService,
        private sessionCrypto: SessionCryptoService,
    )
    {
        super();
    }

    public getMessages(): Observable<IGroupChatUiMessage[]>
    {
        return this.adapter.getMessages();
    }

    public getMessagesValue(): IGroupChatUiMessage[]
    {
        return this.adapter.getMessagesValue();
    }

    public getUsers(): Observable<IGroupChatUser[]>
    {
        return this.adapter.getUsers();
    }

    public sendMessage(text: string, parentId?: number | null): Promise<IGroupChatSendResult>
    {
        return this.adapter.sendMessage(text, parentId);
    }

    public deleteMessage(id: number): Promise<void>
    {
        return this.adapter.deleteMessage(id);
    }

    public deleteMessages(ids: number[]): Promise<void>
    {
        return this.adapter.deleteMessages(ids);
    }

    public clearAll(): Promise<void>
    {
        return this.adapter.clearAll();
    }

    public loadUsers(): Promise<void>
    {
        return this.adapter.loadUsers();
    }

    public $isMessagesLoading(): Observable<boolean>
    {
        return this.adapter.$isMessagesLoading();
    }

    public override $isIdentityUnavailable(): Observable<boolean>
    {
        return this.adapter.$isIdentityUnavailable();
    }

    public override $needsMailConsent(): Observable<boolean>
    {
        return this.adapter.$needsMailConsent();
    }

    public override $documentId(): Observable<string | null>
    {
        return this.adapter.$documentId();
    }

    public override requestMailConsent(): Promise<void>
    {
        return this.adapter.requestMailConsent();
    }

    public $isAvailable(): Observable<boolean>
    {
        return this.adapter.$isAvailable();
    }

    public getFirstUnreadMessageId(): number | null
    {
        return this.adapter.getFirstUnreadMessageId();
    }

    public $firstUnreadId(): Observable<number | null>
    {
        return this.adapter.$firstUnreadId();
    }

    public $unreadCount(): Observable<number>
    {
        return this.adapter.$unreadCount();
    }

    public setWindowActive(isActive: boolean): void
    {
        this.adapter.setWindowActive(isActive);
    }

    public setViewActive(isActive: boolean): void
    {
        this.adapter.setViewActive(isActive);
    }

    public setAtBottom(isAtBottom: boolean): void
    {
        this.adapter.setAtBottom(isAtBottom);
    }

    public startPolling(): void
    {
        this.adapter.startPolling();
    }

    public stopPolling(): void
    {
        this.adapter.stopPolling();
    }

    public init(): Promise<void>
    {
        return this.adapter.init();
    }

    public override onDestroy(): void
    {
        this.adapter.onDestroy();
    }
}
